import * as React from "react";
import {GridColDef} from "@mui/x-data-grid";
import {formatTime, stringAvatar} from "@/helper/method";
import {BtnType, ProTable} from "@/widget/ProTable/ProTable";
import {CopyrightPageVO, useQueryCopyrightPage} from "@/service/copyright.s";
import {WriteCopyright} from "@/pages/Copyright/WriteCopyright";
import {useParams} from "react-router-dom";
import {useEffect, useMemo} from "react";
import {ViewCopyright} from "@/pages/Copyright/ViewCopyright";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {Avatar, ButtonGroup} from "@mui/material";
import Button from "@mui/material/Button";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Chip from "@mui/material/Chip";
import CopyrightOutlinedIcon from '@mui/icons-material/CopyrightOutlined';
import {useClaimProduct} from "@/service/contract.s";
import {useGlobalActions} from "@/store/global.c";
import {defaultPageParams, permScope, roleAlias} from "@/helper/constant";
import {useAuthInfo} from "@/store/account.c";
import {SellerAvatar} from "@/widget/SellerAvatar/SellerAvatar";

const copyrightCode: { [key: string]: string } = {
    'all': "all_copyright",
    "unclaim": "unclaim_copyright",
    "unassigned": "unassigned_copyright",
}

const copyrightStatus: { [key: string]: number } = {
    'all': 0,
    "unclaim": 5,
    "unassigned": 4
}

const initPage = (page: string): [number, string] => {
    return [copyrightStatus[page], copyrightCode[page]]
}

export const CopyrightListPage = () => {
    const {page} = useParams()
    const [writeStatus, code] = initPage(page!)
    const {mutateAsync: claimProductMutate} = useClaimProduct()
    const {setToast} = useGlobalActions()
    const {role: {dataScope, roleAlias: alias}} = useAuthInfo()
    const {
        mutate: copyrightPageMutate,
        data: copyrightPageData,
        isPending: copyrightPagePending,
        isIdle
    } = useQueryCopyrightPage(writeStatus)

    const allColumns: GridColDef<CopyrightPageVO>[] = [
        {
            field: "copyrightName",
            headerName: "版权名称",
            headerAlign: "left",
            align: "left",
            flex: 0.6,
            valueFormatter: (value: string) => {
                return value || "待定"
            }
        },
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {field: "customerName", headerName: "客户名称", flex: 0.6},
        {
            field: "category",
            headerName: "版权类型",
            headerAlign: "left",
            align: "left",
            flex: 0.4
        },
        {
            field: "copyrightStatus",
            headerName: "版权状态",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value: string) => value || '-'
        },
        {
            field: "receiveTime",
            headerName: "领取时间",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => formatTime("D", value) || "-",
        },
        {
            field: "writer",
            headerName: "撰稿人",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: 'flex',
            renderCell: ({row}) => <Avatar {...stringAvatar(row.writer)} />
        },
        {
            field: "createdAt",
            headerName: "内部期限",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
    ];

    const unassignedColumns: GridColDef<CopyrightPageVO>[] = [
        {
            field: "copyrightName",
            headerName: "版权名称",
            headerAlign: "left",
            align: "left", flex: 0.4,
            valueFormatter: (value: string) => value || '-'
        },
        {
            field: "category",
            headerName: "类型",
            headerAlign: "left",
            align: "left", flex: 0.4,
            valueFormatter: () => "版权注册",
        },
        {field: "customerName", headerName: "客户名称", flex: 0.6},
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "num",
            headerName: "数量",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "price",
            headerName: "金额",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "dealTime",
            headerName: "成交时间",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
        {
            field: "createdAt",
            headerName: "创建时间",
            headerAlign: "right",
            align: "right",
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    const unclaimColumns: GridColDef<CopyrightPageVO>[] = [
        {
            field: "kind",
            headerName: "服务类型",
            headerAlign: "left",
            align: "left", flex: 0.4,
        },

        {
            field: "category",
            headerName: "版权类型",
            headerAlign: "left",
            align: "left", flex: 0.4,
        },
        {field: "customerName", headerName: "客户名称", flex: 0.6},
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "assigner",
            headerName: "分配人",
            headerAlign: "center",
            align: "center",
            flex: 0.4
        },
        {
            field: "price",
            headerName: "单价",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "assignTime",
            headerName: "分配时间",
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    useEffect(() => {
        if (isIdle == false) {
            copyrightPageMutate({current: 1, size: 20})
        }
    }, [writeStatus]);


    const columns = useMemo(() => {
        switch (writeStatus) {
            case 0:
                return allColumns
            case 4:
                return unassignedColumns
            case 5:
                return unclaimColumns
        }
        return []
    }, [writeStatus]);

    const btnCallback = async ({btn, keys, onSearch}: BtnType<CopyrightPageVO>) => {
        if (btn.alias == "claim") {
            if (keys.length <= 10 && keys.length > 0) {
                const result = await claimProductMutate({ids: keys, productTypeId: 4})
                if (result.code == 0) {
                    onSearch && onSearch(defaultPageParams)
                    setToast({message: result.msg, open: true, severity: 'success'})
                }
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
    }

    return (
        <ProTable<CopyrightPageVO>
            loading={copyrightPagePending}
            columnVisibilityModel={{
                seller: !(alias == roleAlias["marketing"] && dataScope == permScope['self']),
                writer: !(alias == roleAlias["processer"] && dataScope == permScope['self'])
            }}
            renderDrawer={{view: <ViewCopyright/>, write: <WriteCopyright/>}}
            columns={columns}
            btnCallback={btnCallback}
            showToolbar={writeStatus !== 4}
            drawerProps={{
                view: {
                    bodyWidth: 580, headerHeight: 68, hiddenFooter: true, viewBtnCallback: (row: CopyrightPageVO) => {
                        return {
                            custom: true,
                            title: row.copyrightName || "待定",
                            businessId: row.contractId,
                            switchButton: <ButtonGroup size={'small'} sx={{
                                "& .MuiButton-startIcon": {marginRight: 0},
                                "& .MuiButton-endIcon": {marginLeft: 0}
                                , "& .MuiButtonGroup-grouped": {minWidth: 20, height: 20, width: 12, bgcolor: '#f2f4fb',}
                            }} variant="outlined">
                                <Button size={'small'} startIcon={<NavigateBeforeIcon/>}/>
                                <Button size={'small'} endIcon={<NavigateNextIcon/>}/>
                            </ButtonGroup>,
                            icon: <CopyrightOutlinedIcon color={'info'} fontSize={'small'}/>,
                            tags: <Stack direction={'row'} spacing={1.4} alignContent={'center'}>
                                <Chip size={'small'} variant={'outlined'} sx={{height: 16}} color={'warning'} label={<Typography fontSize={10}>{row.copyrightStatus}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'error'} label={<Typography fontSize={10}>{row.nodeNames}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'success'}
                                      label={<Typography fontSize={10}>{row.copyrightStatus || '-'}</Typography>}/>
                            </Stack>, instanceId: row.instanceId
                        }
                    }
                }
            }}
            data={copyrightPageData?.data}
            onSearch={(params) => {
                copyrightPageMutate(params);
            }}
            code={code}
        />
    );
};
